import { ComboboxOption } from 'components/ui'
import { useApp } from 'context/app-context'
import { useCurrentUser } from 'context/current-user-context'
import useApi from 'hooks/use-api'
import useDebounce from 'hooks/use-debounce'
import { usePaginationParams } from 'hooks/use-pagination-params'
import { useRouter } from 'next/router'
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'
import useSWR from 'swr'
import { UnitShorthand } from 'types/unit-shorthand'

type ToolLibrary = {
  success?: boolean
  fileName: string
  error?: string
}

interface ContextInterface {
  isForward: boolean
  step: number
  nextStep: () => void
  previousStep: (steps?: number) => void
  close: () => void
  isVisible: boolean
  toolLibrary: ToolLibrary
  setToolLibrary: Dispatch<SetStateAction<ToolLibrary>>
  toolLibraryFile: File
  setToolLibraryFile: Dispatch<SetStateAction<File>>
  unit: UnitShorthand
  setUnit: Dispatch<SetStateAction<UnitShorthand>>
  material: ComboboxOption
  setMaterial: Dispatch<SetStateAction<ComboboxOption>>
  handleDeleteToolLibrary: () => Promise<void>
  partFile: File
  setPartFile: Dispatch<SetStateAction<File>>
  partError: string
  setPartError: Dispatch<SetStateAction<string>>
  projectUuid: string
  handleUseSampleToolLibrary: () => Promise<void>
  handleDeletePart: () => void
  handleDeleteCutConfig: () => Promise<void>
  onboard: (samplePartName?: string) => Promise<void>
  isCompleting: boolean
}

const OnboardingContext = createContext<Partial<ContextInterface>>(
  {} as ContextInterface,
)

interface ProviderProps {
  children: ReactNode
}

const OnboardingProvider: FC<ProviderProps> = ({ children }) => {
  const currentUser = useCurrentUser()
  const router = useRouter()
  const api = useApi()
  const { setNotification } = useApp()
  const [step, setStep] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [isForward, setIsForward] = useState(true)
  const [toolLibraryFile, setToolLibraryFile] = useState<File>(null)
  const [partFile, setPartFile] = useState<File>(null)
  const [toolLibrary, setToolLibrary] = useState<ToolLibrary>(null)
  const [material, setMaterial] = useState<ComboboxOption>(null)
  const [projectUuid, setProjectUuid] = useState<string>(null)
  const [partError, setPartError] = useState<string>(null)
  const [isCompleting, setIsCompleting] = useState(false)
  const {
    query: { page = '1', search, tags, users },
    buildSWRUrl,
  } = usePaginationParams()
  const url = buildSWRUrl('/api/projects', { page, search, tags, users })
  const onProjectsPage = router.pathname.startsWith('/projects')
  const { mutate: mutateProjects } = useSWR(onProjectsPage && isVisible && url)
  const unit = currentUser.defaultUnit
  const setUnit = useCallback(
    (newUnit: UnitShorthand) => {
      if (newUnit !== currentUser.defaultUnit) {
        currentUser.toggleDefaultUnit()
      }
    },
    [currentUser],
  )

  // Use a short delay to trigger the initial fade/zoom in transition.
  useDebounce(
    () => {
      if (!currentUser || currentUser.isExpired) {
        return
      }

      setIsVisible(currentUser.showOnboarding)
    },
    [currentUser],
    75,
  )

  const previousStep = (steps: number = 1) => {
    setStep((currentStep) => {
      setIsForward(false)
      return currentStep - steps
    })
  }

  const nextStep = () => {
    setStep((currentStep) => {
      setIsForward(true)
      return currentStep + 1
    })
  }

  const close = () => {
    currentUser.hideOnboarding()
    currentUser.refreshUser()

    if (onProjectsPage) {
      mutateProjects()
    } else {
      router.push('/projects')
    }

    setIsVisible(false)
  }

  const handleDeleteToolLibrary = async () => {
    setToolLibrary(null)
    setToolLibraryFile(null)
  }

  const handleDeletePart = () => {
    setPartFile(null)
    setPartError(null)
  }

  const handleUseSampleToolLibrary = async () => {
    const fileName = `sample_${material.id}_${unit}.tools`
    setToolLibrary({ fileName, success: true })
  }

  // Create tool library, cut config, and first project.
  const onboard = async (samplePartName?: string) => {
    setIsCompleting(true)
    let toolLibraryUuid: string

    try {
      if (toolLibraryFile) {
        const formData = new FormData()
        formData.append('file', toolLibraryFile)
        const response: { uuid: string } = await api(
          `/api/tool-libraries?runPreTool=1`,
          'POST',
          formData,
        )
        toolLibraryUuid = response.uuid
      }

      const onboardingResponse: { projectUuid: string } = await api(
        `/api/onboard`,
        'POST',
        {
          unit,
          material: material.id,
          materialName: material.value,
          toolLibraryUuid,
          samplePartName,
        },
      )

      setNotification(null)

      if (!samplePartName) {
        setProjectUuid(onboardingResponse.projectUuid)
      } else {
        close()
      }
    } catch (e) {
      setIsCompleting(false)
    }
  }

  return (
    <OnboardingContext.Provider
      value={{
        isForward,
        step,
        previousStep,
        nextStep,
        close,
        isVisible,
        toolLibrary,
        setToolLibrary,
        toolLibraryFile,
        setToolLibraryFile,
        partFile,
        setPartFile,
        partError,
        setPartError,
        projectUuid,
        unit,
        setUnit,
        material,
        setMaterial,
        handleUseSampleToolLibrary,
        handleDeleteToolLibrary,
        handleDeletePart,
        onboard,
        isCompleting,
      }}>
      {children}
    </OnboardingContext.Provider>
  )
}

const useOnboarding = () => useContext(OnboardingContext)
const OnboardingConsumer = OnboardingContext.Consumer
export {
  useOnboarding,
  OnboardingProvider,
  OnboardingContext,
  OnboardingConsumer,
}
